import * as React from "react";
import {
  HouseWorkType,
  InvoiceCreatorState,
  InvoiceData,
  InvoiceRow,
  InvoiceRowType,
  InvoiceStatus,
} from "../fortnox/invoice_creator_client";
import styled from "styled-components";
import numbro from "numbro";

interface Props {
  invoice: InvoiceData | null;
  confirmExportInvoices: (invoiceIds: number[]) => void;
}

const InvoiceTableWrapper = styled.div`
  min-height: 500px;

  & > h2 {
    font-size: 1rem;
  }

  & table {
    max-width: 1000px;

    & tbody tr:not(:first-child) {
      & td {
        border-top: 1px solid black;
      }
    }

    & tr {
      // Article number
      & td:nth-child(1) {
        width: 100px;
      }

      // Description
      & td:nth-child(2) {
        //width: 100%;
      }

      // House work
      & td:nth-child(3) {
        width: 100px;
      }

      // Unit
      & td:nth-child(4) {
        width: 100px;
      }

      // Quantity
      & td:nth-child(5),
      & th:nth-child(5) {
        width: 100px;
        text-align: right;
      }

      // Price
      & td:nth-child(6),
      & th:nth-child(6) {
        width: 100px;
        text-align: right;
      }
    }

    // Total price
    & tr.table-footer {
      text-align: right;
    }
  }

  & .inputs {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 0.5rem;
    margin: 0;

    & > * {
      margin: 0;
    }
  }

  & .houseWorkType {
    font-size: 0.7rem;
    background-color: rgba(0, 0, 0, 0.1);
    padding: 3px;
    border-radius: 2px;
    margin-left: 0.1rem;
  }

  & .error-report {
    background-color: rgba(255, 0, 0, 0.1);
    margin: 10px;
    margin-left: 0;
    max-width: 1000px;
    padding: 10px;
    border-radius: 2px;
    border: 1px solid #d10000;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

// Keep this synced with the number of header columns in renderHeaderRow.
const NUM_HEADER_COLS = 6;

export const formatNumber = (num: number) => {
  return numbro(num).format({ thousandSeparated: true, mantissa: 2 });
};

const InvoiceTable = ({ invoice, confirmExportInvoices }: Props) => {
  const renderHeaderRow = () => {
    return (
      <tr>
        <th>Artikelnr</th>
        <th>Benämning</th>
        <th>Husarbete</th>
        <th>Enhet</th>
        <th>Antal</th>
        <th>
          À-pris
          <br />
          (ex moms)
        </th>
      </tr>
    );
  };

  const renderFooterRows = () => {
    return (
      invoice && (
        <>
          <tr className="table-footer">
            <td colSpan={5}>
              <span>Summa (ex moms)</span>
            </td>
            <td>
              <strong>{invoice && formatNumber(invoice.priceSum)}</strong>
            </td>
          </tr>
          <tr className="table-footer">
            <td colSpan={5}>
              <span>Summa (inkl moms)</span>
            </td>
            <td>
              <strong>{formatNumber(invoice.priceSumIncludingVAT)}</strong>
            </td>
          </tr>
          {invoice?.taxReductionSum && (
            <>
              <tr className="table-footer">
                <td colSpan={5}>
                  <span>Summa husarbete (inkl moms)</span>
                </td>
                <td>
                  <strong>
                    {formatNumber(invoice.houseWorkSumIncludingVAT)}
                  </strong>
                </td>
              </tr>
              <tr className="table-footer">
                <td colSpan={5}>
                  <span>Skattereduktion</span>
                </td>
                <td>
                  <strong>{formatNumber(invoice?.taxReductionSum)}</strong>
                </td>
              </tr>
              <tr className="table-footer">
                <td colSpan={5}>
                  <span>Att betala</span>
                </td>
                <td>
                  <strong>
                    {formatNumber(
                      Math.round(
                        invoice?.priceSumIncludingVAT - invoice.taxReductionSum,
                      ),
                    )}
                  </strong>
                </td>
              </tr>
            </>
          )}
        </>
      )
    );
  };

  const renderRow = (row: InvoiceRow, rowIndex: number) => {
    return (
      <tr key={rowIndex}>
        {(row.rowType == InvoiceRowType.Text ||
          row.rowType == InvoiceRowType.EmptyRow) && (
          <>
            <td></td>
            <td colSpan={NUM_HEADER_COLS - 1}>
              <span>
                {(row.rowType == InvoiceRowType.Text && row.text) || (
                  <>&nbsp;</>
                )}
              </span>
            </td>
          </>
        )}
        {row.rowType == InvoiceRowType.Article && (
          <>
            <td>{row.articleNo}</td>
            <td>{row.name}</td>
            <td>
              {row.houseWork ? "Ja" : "Nej"}
              {row.houseWork && (
                <span className="houseWorkType">
                  {houseWorkTypeToString(row.houseWorkType)}
                </span>
              )}
            </td>
            <td>{row.unitName}</td>
            <td>{formatNumber(row.quantity).toLocaleString()}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              {formatNumber(row.totalPrice).toLocaleString()}
            </td>
          </>
        )}
      </tr>
    );
  };

  return (
    <InvoiceTableWrapper>
      {invoice && (
        <>
          <h2>
            {invoice.customerName} ({invoice.customerNo})
          </h2>
          <table cellSpacing={0}>
            <thead>{renderHeaderRow()}</thead>
            <tbody>
              {invoice.rows.map(renderRow)}
              {renderFooterRows()}
            </tbody>
          </table>
          <div className="inputs">
            <button
              className="button primary"
              onClick={() => confirmExportInvoices([invoice.id])}
              disabled={invoice.status == InvoiceStatus.Sent}
            >
              <i className="fa fa-paper-plane"></i> Skicka till Fortnox
            </button>
            {invoice.status == InvoiceStatus.Sent && (
              <span>
                <i className="fa fa-check"></i> Skapad
                {invoice.fortnoxInvoiceUrl && (
                  <>
                    {" - "}
                    <a href={invoice.fortnoxInvoiceUrl} target="_blank">
                      Visa i Fortnox
                    </a>
                  </>
                )}
              </span>
            )}
          </div>
          {invoice.status == InvoiceStatus.Error && invoice.exportError && (
            <div className="error-report">
              <h4>Ett fel uppstod vid skapandet av fakturan</h4>
              <p>
                Kontrollera i Fortnox om fakturan skapades. Om den inte skapats
                kan du pröva att klicka på skicka till Fortnox igen.
              </p>
              <h5>Felmeddelande</h5>
              <p>{invoice.exportError}</p>
            </div>
          )}
          {invoice.createTaxReductionError != null && (
            <div className="error-report">
              <h4>Ett fel uppstod vid skapandet av skattereduktionen</h4>
              <p>
                Öppna fakturan i Fortnox och spara om den för att skapa
                skattereduktionen manuellt.
              </p>
              <h5>Felmeddelande</h5>
              <p>{invoice.createTaxReductionError}</p>
            </div>
          )}
        </>
      )}
    </InvoiceTableWrapper>
  );
};

function houseWorkTypeToString(type: HouseWorkType): string {
  switch (type) {
    case HouseWorkType.Construction:
      return "Bygg";
    case HouseWorkType.Electricity:
      return "El";
    case HouseWorkType.GlassMetalWork:
      return "Glas/plåtarbete";
    case HouseWorkType.GroundDrainageWork:
      return "Mark/dräneringsarbete";
    case HouseWorkType.Masonry:
      return "Murning";
    case HouseWorkType.PaintingWallpapering:
      return "Målning/tapetsering";
    case HouseWorkType.HVAC:
      return "VVS";
    case HouseWorkType.MajorApplianceRepair:
      return "Reparation av vitvaror";
    case HouseWorkType.MovingServices:
      return "Flyttjänster";
    case HouseWorkType.ITServices:
      return "IT-tjänster";
    case HouseWorkType.Cleaning:
      return "Städning";
    case HouseWorkType.TextileClothing:
      return "Kläd- och textilvård";
    case HouseWorkType.Snowplowing:
      return "Snöskottning";
    case HouseWorkType.Gardening:
      return "Trädgårdsarbete";
    case HouseWorkType.Babysitting:
      return "Barnpassning";
    case HouseWorkType.OtherCare:
      return "Personlig omsorg";
    case HouseWorkType.OtherCosts:
      return "Övrig kostnad";
    case HouseWorkType.Cooking:
      return "Matlagning";
    case HouseWorkType.Tutoring:
      return "Läxhjälp";
    case HouseWorkType.SolarCells:
      return "Solceller";
    case HouseWorkType.StorageSelfProducedElectricity:
      return "Lagring egenproducerad el";
    case HouseWorkType.ChargingStationElectricVehicle:
      return "Laddningspunkt elfordon";
    case HouseWorkType.HomeMaintenance:
      return "Tillsyn av bostad";
    case HouseWorkType.Furnishing:
      return "Möblering";
    case HouseWorkType.TransportationServices:
      return "Transporttjänster";
    case HouseWorkType.WashingAndCareOfClothing:
      return "Tvätt och vård av kläder";
    case HouseWorkType.EmptyHousework:
      return "Ej husarbete";
    case HouseWorkType.Blank:
      return "";
  }
}

export default InvoiceTable;
